import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi, apiPost } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Crown, Shield, Wallet, Plug, ScrollText, FileCheck2, Radar, Cpu, BarChart3, User, Lock, LogIn, ArrowLeft } from "lucide-react";

const ICONS = { crown: Crown, shield: Shield, wallet: Wallet, plug: Plug, scroll: ScrollText, filecheck: FileCheck2, radar: Radar, cpu: Cpu, chart: BarChart3, user: User };

export default function Login() {
  const { data: profiles = [] } = useApi("profiles", "/auth/profiles");
  const { login } = useApp();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const signIn = async () => {
    setBusy(true);
    try {
      const r = await apiPost("/auth/login", { profile_id: selected.id, password: pwd });
      login(r.user); toast.success(`Welcome, ${r.user.name.split(" ")[0]}`); navigate("/os");
    } catch (e) { toast.error(e?.response?.data?.detail || "Sign-in failed"); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen navitra-bg flex flex-col" style={{ background: "linear-gradient(160deg, hsl(208 40% 92%), hsl(40 33% 97%) 55%, hsl(43 40% 90%))" }}>
      <div className="flex items-center justify-between px-6 h-16">
        <span className="brand-chip"><img src="/brand/navitra-logo.webp" alt="Navitra OS" className="h-9" /></span>
        <div className="flex items-center gap-4">
          <div className="flex rounded-full border bg-white/70 px-1 py-0.5 text-xs font-medium"><span className="rounded-full bg-navy text-white px-3 py-1">EN</span><span className="px-3 py-1 text-muted-foreground">AR</span></div>
          <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground hidden md:inline">Multi-user sign-in · Select a profile</span>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
        {!selected ? (
          <>
            <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="font-display text-3xl md:text-4xl text-navy mb-1.5">Welcome to Navitra OS</motion.h1>
            <p className="text-sm text-muted-foreground mb-8">Select your profile to sign in.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-4xl w-full">
              {profiles.map((p, i) => { const Icon = ICONS[p.icon] || User; return (
                <button key={p.id}
                  onClick={() => { setSelected(p); setPwd(""); }} data-testid={`profile-${p.id}`}
                  className="rounded-xl border bg-white/80 p-3.5 flex flex-col items-center text-center hover:shadow-md hover:-translate-y-0.5 hover:border-gold transition-all">
                  <span className="h-10 w-10 rounded-full flex items-center justify-center mb-2 shadow-sm" style={{ background: p.color }}><Icon className="h-4 w-4 text-white" /></span>
                  <p className="font-semibold text-sm text-ink leading-tight">{p.name}</p>
                  <p className="text-[10px] uppercase tracking-wide text-navy font-medium mt-0.5">{p.role}</p>
                  <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{p.subtitle}</p>
                </button>
              ); })}
            </div>
            <button onClick={() => navigate("/")} className="mt-8 text-sm text-muted-foreground hover:text-navy" data-testid="back-home">← Back to home</button>
          </>
        ) : (
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-sm flex flex-col items-center">
            <button onClick={() => setSelected(null)} className="self-center mb-5 text-sm text-muted-foreground hover:text-navy flex items-center gap-1" data-testid="back-users"><ArrowLeft className="h-4 w-4" />All users</button>
            {(() => { const Icon = ICONS[selected.icon] || User; return <span className="h-16 w-16 rounded-full flex items-center justify-center mb-3 shadow-lg" style={{ background: selected.color }}><Icon className="h-6 w-6 text-white" /></span>; })()}
            <p className="font-display text-xl text-ink">{selected.name}</p>
            <p className="text-[11px] uppercase tracking-widest text-navy font-medium mt-1">{selected.role}</p>
            <p className="text-sm text-muted-foreground mt-1 mb-5">{selected.subtitle}</p>
            <div className="w-full flex items-center gap-2 rounded-xl border bg-white/90 px-3 py-2 mb-3">
              <Lock className="h-4 w-4 text-muted-foreground" />
              <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} onKeyDown={(e) => e.key === "Enter" && signIn()} placeholder="Password" className="border-0 shadow-none focus-visible:ring-0 px-1" data-testid="password-input" autoFocus />
            </div>
            <Button className="w-full" size="lg" onClick={signIn} disabled={busy} data-testid="signin-btn"><LogIn className="h-4 w-4 mr-2" />{busy ? "Signing in…" : "Sign in"}</Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
